import React, { useEffect, useState } from "react";

//* MUI Components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";

//* NDBX Components
import { NotificationCard, Section, RHFCheckbox } from "@arp/react-ndbx";

//* Page Level Components
import RHFAutocompleteChip from "../../coding/rhfAutocompleteChip/RHFAutocompleteChip";

//* Config
import { percentageItems } from "../../../config/dropdownSelectValues/selectValues";
import { additionalInformationNotification } from "../../../config/notifications/pageNotifications";
// import { contactCenterPolicyOutcomeFieldNames } from "../../../config/pageInfo/pageFieldName.js";

//* Context
import usePreQualification from "../../../hooks/usePreQualification.ts";
import useNewBusiness from "../../../hooks/useNewBusiness.ts";
import useSharedData from "../../../hooks/useSharedData.ts";

//* Hooks
import { useUpdateConditionsMutation } from "../../../hooks/dataHooks/useUpdateConditionsMutation.js";

//----------------------------------------------------------------

//! TODO: Try to move all the updates in to the component.
//! TODO: First make sure that when we update the usePreQualification
//! TODO: and the newBusiness contexts, that these are reflected in
//! TODO: the parent component (SuccessfulOutcomeDetail.jsx)
//! TODO: FOR APRIL 2nd 2024.

export default function AdditionalInformation({ ...props }) {
	const {
		dataValues,
		name,
		coverType,
		showBackdrop,
		editChecked,
		backgroundColor = "#FAF7EF",
		maxWidth = "690px",
		resetValues = false, //* Rest the component from parent
	} = { ...props };

	const [percentageValueItems, setPercentageValueItems] = useState([]);
	const [exclusionValueItems, setExclusionValueItems] = useState([]);

	//* Context Initialization Start
	const { funcSet, dataSet, state } = usePreQualification();
	//* Shared data
	const {
		accessToken,
		additionalConditionsChanged,
		handleAdditionalConditionsChange,
	} = useSharedData();

	//* set the values we used to display values based on the Cover Type
	const coverDisplayName = "additional" + coverType + "LoadingDisplay";
	const coverLoadingAmount = "additional" + coverType + "LoadingAmount";
	const hasAdditionalInfo = "hasAdditional" + coverType + "ExclusionOrLoadings";

	const coverExclusionsDisplay = "additional" + coverType + "ExclusionsDisplay";
	const coverExclusionsValues = "additional" + coverType + "ExclusionsValues";
	const excludedAuraExclusions = "excluded" + coverType + "Exclusions";

	useEffect(() => {
		//* Set the percentage dropdown values on page load.
		setPercentageValueItems(percentageItems);

		//* Only populate the exclusion list dropdown
		//* with values that have not already been excluded.
		// setExclusionValueItems(
		// 	dataValues.filter(
		// 		(el) => !outcomeData?.covers?.excludedLifeExclusions?.includes(el.name)
		// 	)
		// );

		setExclusionValueItems(
			dataValues.filter(
				(el) =>
					!outcomeData?.covers?.[excludedAuraExclusions]?.includes(el.name)
			)
		);

		//* Set the additional (loading/exclusions) values (if there are any) on load
		applyUpdateConditions({
			updateData: outcomeData ? outcomeData?.covers?.[coverLoadingAmount] : 0,
			updateType: "update_loading",
		});
		applyUpdateConditions({
			updateData: outcomeData
				? outcomeData?.covers?.[coverExclusionsValues]
				: [],
			updateType: "update_exclusions",
		});
	}, []);

	const {
		data: outcomeData,
		updateCoverExclusionsData,
		isDataUpdating: outcomeIsLoading,
	} = useNewBusiness();

	const {
		mutate: updateConditions,
		data: conditionsData,
		isLoading,
	} = useUpdateConditionsMutation();

	const applyChange = () => {
		const additionalConditionsNode = dataSet.additionalConditions;
		updateConditions({
			updateData: additionalConditionsNode,
			accessToken: accessToken,
		});
	};

	useEffect(() => {
		showBackdrop(isLoading);
		if (isLoading === true || conditionsData == null) return;
		updateCoverExclusionsData(conditionsData);
	}, [isLoading]);

	//* Context Initialization Finish
	const [loadingPercentageDisplay, setLoadingPercentageDisplay] = useState({
		display: `${outcomeData?.covers?.[coverDisplayName]}`,
	});

	const [showMessage, setShowMessage] = useState(
		outcomeData ? outcomeData?.covers?.[hasAdditionalInfo] : false
	);
	const [enableControls, setEnableControls] = useState(
		outcomeData ? !outcomeData?.covers?.[hasAdditionalInfo] : false
	);

	const [clearValues, setClearValues] = useState(false);

	// ? ***********************************************************
	const [defaultCheckedValue, setDefaultCheckedValue] = useState(
		outcomeData ? outcomeData?.covers?.[hasAdditionalInfo] : false
	);

	const onEditClick = (e, val) => {
		const targetName = e?.target?.name;
		editChecked(targetName, val);
		setDefaultCheckedValue((prev) => !prev);
		setShowMessage(val);
		setEnableControls(!val);
		setClearValues(!val);
		setLoadingPercentageDisplay("");
	};

	//* Reset the component from the parent component.
	useEffect(() => {
		if (resetValues === false) return;

		const targetName = name; //! Do we need this?
		editChecked(targetName, false); //! Do we need this?
		setShowMessage(false);
		setEnableControls(true);
		setClearValues(true);
		setLoadingPercentageDisplay("");
		setDefaultCheckedValue(false);
	}, [resetValues]);
	// ? ***********************************************************

	useEffect(() => {
		const resetExclusions = async () => {
			if (clearValues === false) return;

			//* Clear both the additional percentage and exclusions
			//* from the PreQualification dataset object
			await applyUpdateConditions({
				updateData: 0,
				updateType: "update_loading",
			});

			await applyUpdateConditions({
				updateData: [],
				updateType: "update_exclusions",
			});
		};

		resetExclusions();
	}, [clearValues]);

	const onControlClick = (e) => {
		e.preventDefault();
		if (showMessage === true) {
			// setShowMessage(false);
		}
	};

	//** Handle the loadings being updated */
	const handleLoadingOnBlur = async (value) => {
		const amt =
			value[0] != null && value[0][0]?.value != null ? value[0][0]?.value : 0;
		await applyUpdateConditions({
			updateData: amt,
			updateType: "update_loading",
		});
	};

	//** Handle the exclusions being updated */
	const handleExclusionsOnBlur = async (value) => {
		let exclusionArray = value.map((item) => {
			return item.name;
		});

		await applyUpdateConditions({
			updateData: exclusionArray,
			updateType: "update_exclusions",
		});
	};

	const applyUpdateConditions = async ({ updateData, updateType }) => {
		let updateObject = {
			data: updateData,
			type: updateType,
			fieldName: name,
		};
		//* Tell the rest of the app that some
		//* additional value has changed, through the Shared Data Context
		handleAdditionalConditionsChange();
		funcSet.updateAdditionalConditions(updateObject);
	};

	useEffect(() => {
		if (
			state?.additionalConditionsUpdating == null ||
			state?.additionalConditionsUpdating === true
		)
			return;
		applyChange();
	}, [state?.additionalConditionsUpdating]);

	return (
		<Card elevation={0} sx={{ backgroundColor: backgroundColor }}>
			<Divider sx={{ maxWidth: "90%" }} />
			<CardContent
				sx={{
					maxWidth: { maxWidth },
					p: 3,
				}}
			>
				<Section sx={{ display: "flex", justifyContent: "flex-start" }}>
					<Stack
						direction={"row"}
						spacing={0}
						justifyContent="flex-start"
						alignItems="center"
						width="100%"
					>
						<Section
							sx={{
								display: "flex",
								justifyContent: "flex-start",
							}}
						>
							<Typography variant="h3">Additional Information</Typography>
						</Section>

						<Section
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								alignItems: "center",
								marginRight: -2,
							}}
						>
							<RHFCheckbox
								label={"Edit"}
								defaultChecked={defaultCheckedValue}
								checked={defaultCheckedValue}
								name={`${name}-Edit-Checkbox`}
								onChange={(e) => onEditClick(e, !showMessage)}
								sx={{ margin: 100 }}
								// ref={checkboxRef}
							></RHFCheckbox>
						</Section>
					</Stack>
				</Section>
				<Section sx={{ outline: "0px solid green" }}>
					<RHFAutocompleteChip
						name={`${name}-Loadings`}
						dataValues={percentageValueItems}
						defaultValue={clearValues === true ? "" : loadingPercentageDisplay}
						onFocus={(e) => onControlClick(e)}
						onBlur={(value) => handleLoadingOnBlur(value)}
						disabled={enableControls}
						multiple={false}
						label="Loadings"
						placeholder="Select a loading"
						clearValue={clearValues}
					/>
				</Section>
				<Section sx={{ outline: "0px solid blue" }}>
					<RHFAutocompleteChip
						name={`${name}-Exclusions`}
						dataValues={exclusionValueItems}
						defaultValue={
							clearValues === true
								? []
								: outcomeData?.covers?.[coverExclusionsDisplay]
						}
						onFocus={(e) => onControlClick(e)}
						onBlur={(value) => handleExclusionsOnBlur(value)}
						disabled={enableControls}
						label="Exclusions"
						placeholder="Set Exclusions"
						clearValue={clearValues}
						disableCloseOnSelect={true}
					/>
				</Section>
				<Fade
					in={showMessage === true}
					out={showMessage === false}
					timeout={500}
				>
					<div>
						<Section
							sx={{
								mt: 2,
								display: showMessage === true ? "block" : "none",
							}}
						>
							<NotificationCard
								cardType={
									additionalInformationNotification
										.ADDITIONAL_INFORMATION_WARNING.cardType
								}
								title={
									additionalInformationNotification
										.ADDITIONAL_INFORMATION_WARNING.title
								}
								message={
									additionalInformationNotification
										.ADDITIONAL_INFORMATION_WARNING.message
								}
							/>
						</Section>
					</div>
				</Fade>
				<Fade
					in={showMessage === true}
					out={showMessage === false}
					timeout={500}
				>
					<div>
						<Section
							sx={{
								mt: 2,
								display: showMessage === true ? "block" : "none",
							}}
						>
							<NotificationCard
								cardType={
									additionalInformationNotification
										.ADDITIONAL_INFORMATION_NOTIFICATION.cardType
								}
								title={
									additionalInformationNotification
										.ADDITIONAL_INFORMATION_NOTIFICATION.title
								}
								message={
									additionalInformationNotification
										.ADDITIONAL_INFORMATION_NOTIFICATION.message
								}
							/>
						</Section>
					</div>
				</Fade>
			</CardContent>
		</Card>
	);
}
